class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.attachClearAllListeners = this.attachClearAllListeners.bind(this);
    this.updateActiveFiltersLabel = this.updateActiveFiltersLabel.bind(this);
    this.handleMobileFacetsClick = this.handleMobileFacetsClick.bind(this);
    this.updateMobileFacetsCount = this.updateMobileFacetsCount.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', (event) => {
      this.debouncedOnSubmit(event);
      if (event.target.classList.contains('mobile-facets__checkbox')) {
        const filterGroup = event.target.closest('.mobile-facets__details');
        if (filterGroup) {
          this.updateActiveFiltersLabel(filterGroup);
        }
      }
    });

    this.attachClearAllListeners();

    // Add click handler for mobile facets
    const mobileFacets = this.querySelectorAll('.mobile-facets');
    mobileFacets.forEach((facet) => {
      facet.addEventListener('click', this.handleMobileFacetsClick);
    });

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);

    // Update mobile facets count initially
    this.updateMobileFacetsCount();
  }

  attachClearAllListeners() {
    // Remove existing listeners first
    this.querySelectorAll('.mobile-facets__clear-all').forEach((button) => {
      button.removeEventListener('click', this.handleClearAllClick);
    });

    // Attach new listeners
    this.querySelectorAll('.mobile-facets__clear-all').forEach((button) => {
      button.addEventListener('click', this.handleClearAllClick.bind(this));
    });
  }

  handleClearAllClick(event) {
    // Stop both default behavior and propagation
    event.preventDefault();
    event.stopPropagation();

    // Get the details element
    const details = event.currentTarget.closest('details');
    const wasOpen = details?.hasAttribute('open');

    const filterName = event.currentTarget.dataset.filterName;
    this.clearFilterGroup(filterName);

    // Ensure details stays open if it was open
    if (wasOpen) {
      details.setAttribute('open', '');
    }
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.updateMobileFiltersCounter();
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
        this.emitUpdateEvent();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.updateMobileFiltersCounter();
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
    this.emitUpdateEvent();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  static updateMobileFiltersCounter() {
    const mobileFiltersCounter = document.querySelector('.mobile-facets__counter');
    if (mobileFiltersCounter) {
      const activeFilters = document.querySelectorAll('.active-facets__button').length;
      mobileFiltersCounter.textContent = activeFilters === 0 ? '' : `(${activeFilters})`;
    }
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Store filter search values before updating
    const searchInputs = {};
    facetDetailsElementsFromDom.forEach((element) => {
      const filterSearch = element.querySelector('.facets-filter-search__input');
      if (filterSearch) {
        searchInputs[element.id] = filterSearch.value || '';
      }
    });

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);

      // Store the search value before updating the element
      const searchValue = searchInputs[elementToRender.id];

      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }

      // After the element is in the DOM, restore its search state
      const renderedElement = document.getElementById(elementToRender.id);
      if (renderedElement && typeof searchValue !== 'undefined') {
        const filterSearch = renderedElement.querySelector('.facets-filter-search__input');
        if (filterSearch) {
          filterSearch.value = searchValue;
          filterSearch.dataset.previousValue = searchValue;

          // Re-apply the filtering
          const filterSearchElement = filterSearch.closest('filter-search');
          if (filterSearchElement) {
            filterSearchElement.filterItems(searchValue);
          }
        }
      }
    });

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      // Preserve search value for the element being updated
      const searchValue = searchInputs[closestJSFilterID];

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        // Restore search value after counts are updated
        const updatedElement = document.getElementById(closestJSFilterID);
        if (updatedElement && typeof searchValue !== 'undefined') {
          const filterSearch = updatedElement.querySelector('.facets-filter-search__input');
          if (filterSearch) {
            filterSearch.value = searchValue;
            filterSearch.dataset.previousValue = searchValue;

            // Re-apply the filtering
            const filterSearchElement = filterSearch.closest('filter-search');
            if (filterSearchElement) {
              filterSearchElement.filterItems(searchValue);
            }
          }
        }

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';
        if (newElementToActivate && !isTextInput) newElementToActivate.focus({ focusVisible: false });
      }
    }

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    // Reattach all event listeners after rendering
    document.querySelectorAll('facet-filters-form').forEach((form) => {
      form.attachClearAllListeners();

      // Reattach mobile facets click handlers
      const mobileFacets = form.querySelectorAll('.mobile-facets');
      mobileFacets.forEach((facet) => {
        // Remove existing listener first to prevent duplicates
        facet.removeEventListener('click', form.handleMobileFacetsClick);
        facet.addEventListener('click', form.handleMobileFacetsClick);
      });

      setTimeout(() => {
        // Update the mobile facets count after re-render
        form.updateMobileFacetsCount();
      }, 100);
    });
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
    FacetFiltersForm.updateMobileFiltersCounter();
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }

    // Update active filters label after counts are rendered
    const form = target.closest('facet-filters-form');
    if (form) {
      form.updateActiveFiltersLabel(target);
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  static emitUpdateEvent() {
    // emit filter-applied event
    const filterAppliedEvent = new CustomEvent('filter-applied', {
      bubbles: true,
      detail: {},
    });
    document.dispatchEvent(filterAppliedEvent);
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    if (event) {
      event.preventDefault();
    }

    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event && event.srcElement && event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event?.target?.closest('form')?.id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }

  updateActiveFiltersLabel(filterGroup) {
    const checkboxes = filterGroup.querySelectorAll('input[type="checkbox"]');
    const activeFilters = Array.from(checkboxes)
      .filter((checkbox) => checkbox.checked)
      .map((checkbox) => {
        const label = checkbox.nextElementSibling.querySelector('.facet-checkbox__text-label');
        return label ? label.textContent.trim() : '';
      })
      .sort();

    const activeFiltersLabel = filterGroup.querySelector('.mobile-facets__active-filters-label');
    if (activeFiltersLabel) {
      activeFiltersLabel.textContent = activeFilters.length > 0 ? activeFilters.join(', ') + ',' : '';
      activeFiltersLabel.dataset.activeFilters = activeFilters.length;
    }
  }

  clearFilterGroup(filterName) {
    // Find all checkboxes for this filter group
    const filterDetails = this.querySelector(`[data-filter-name="${filterName}"]`);
    if (!filterDetails) return;

    const checkboxes = filterDetails.querySelectorAll('input[type="checkbox"]');
    let wasChanged = false;

    checkboxes.forEach((checkbox) => {
      if (checkbox.checked) {
        checkbox.checked = false;
        wasChanged = true;
      }
    });

    if (wasChanged) {
      // Update the active filters label
      this.updateActiveFiltersLabel(filterDetails);

      // Get the form element
      const form = filterDetails.closest('form');
      if (form) {
        const searchParams = this.createSearchParams(form);
        this.onSubmitForm(searchParams);
      }
    }
  }

  handleMobileFacetsClick(event) {
    // Check if click is outside .mobile-facets__inner
    const inner = event.target.closest('.mobile-facets__inner');
    if (!inner && event.target.classList.contains('mobile-facets')) {
      const details = event.target.closest('details');
      if (details) {
        details.querySelector('summary').click();
      }
    }
  }

  updateMobileFacetsCount() {
    const swatches = document.querySelectorAll('.swatch-group__item');
    if (swatches.length > 0) {
      const totalSwatches = document.querySelectorAll('.swatch-group__item')?.length || 0;
      const mobileFacetsCount = document.querySelector('.mobile-facets__footer-count');
      if (mobileFacetsCount) {
        mobileFacetsCount.textContent = `${totalSwatches}`;
      }
    } else {
      const totalItems = document.querySelectorAll('.collection-grid__item')?.length || 0;
      const mobileFacetsCount = document.querySelector('.mobile-facets__footer-count');
      if (mobileFacetsCount) {
        mobileFacetsCount.textContent = `${totalItems}`;
      }
    }
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

class FilterSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('.facets-filter-search__input');
    this.clearButton = this.querySelector('.facets-filter-search__clear-button');
    this.filterList = this.querySelector('.mobile-facets__list');
    this.searchValue = '';

    // Bind event handlers
    this.handleInput = this.handleInput.bind(this);
    this.handleClear = this.handleClear.bind(this);
    this.filterItems = this.filterItems.bind(this);

    // Add event listeners
    this.input.addEventListener('input', this.handleInput);
    this.clearButton.addEventListener('click', this.handleClear);

    // Restore previous search value if it exists
    if (this.input.dataset.previousValue) {
      this.input.value = this.input.dataset.previousValue;
      this.filterItems(this.input.value);
    }
  }

  handleInput(event) {
    event.stopPropagation();
    const searchValue = event.target.value.toLowerCase().trim();
    this.filterItems(searchValue);

    // Store the search value for persistence
    this.input.dataset.previousValue = searchValue;
  }

  handleClear(event) {
    event.stopPropagation();
    this.input.value = '';
    this.input.dataset.previousValue = '';
    this.filterItems('');
    this.input.focus({ focusVisible: false });
  }

  filterItems(searchValue) {
    const items = this.querySelectorAll('.mobile-facets__item');

    items.forEach((item) => {
      const label = item.querySelector('.facet-checkbox__text-label');
      if (!label) return;

      const text = label.textContent.toLowerCase().trim();
      const shouldShow = text.includes(searchValue);

      item.style.display = shouldShow ? '' : 'none';
    });
  }
}

customElements.define('filter-search', FilterSearch);
